import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  suggestions: [],
  selected_suggestions: null,
  loading: false,
  isSuccess: false,
  recordsTotal: 0,
  errors: [],
};

export const fetchSuggestions = createAsyncThunk('suggestions/fetchSuggestions', async (params, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.get('/Suggestions/all_', { params });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
    }
    if (!error.response) {
      throw error;
    }
    return rejectWithValue(error.response.data);
  }
});

export const getSuggestionById = createAsyncThunk('suggestions/getSuggestionById', async (serviceId, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.get(`/Suggestions/${serviceId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
    }
    if (!error.response) {
      throw error;
    }
    return rejectWithValue(error.response.data);
  }
});

export const deleteSuggestions = createAsyncThunk('suggestions/deleteSuggestion', async (serviceId, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.delete(`/Suggestions/${serviceId}`);
    dispatch(fetchSuggestions());
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
    }
    if (!error.response) {
      throw error;
    }
    return rejectWithValue(error.response.data);
  }
});

// Acceptation : `sourceLang` = langue du nom pour la traduction du service créé ('' = détection auto)
export const updateSuggestion = createAsyncThunk('suggestions/updateSuggestion', async ({ id, sourceLang }, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.put(`/Suggestions/${id}`, sourceLang ? { sourceLang } : {});
    dispatch(fetchSuggestions());
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
    }
    if (!error.response) {
      throw error;
    }
    return rejectWithValue(error.response.data);
  }
});

export const updateSuggestionName = createAsyncThunk('suggestions/updateSuggestionName', async ({ id, name }, { rejectWithValue, dispatch }) => {
  try {
    const response = await api.patch(`/Suggestions/${id}`, { name });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      dispatch(logout());
    }
    if (!error.response) {
      throw error;
    }
    return rejectWithValue(error.response.data);
  }
});

const servicesSlice = createSlice({
  name: 'suggestions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSuggestions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSuggestions.fulfilled, (state, { payload }) => {
        state.isSuccess = true;
        // ✅ FIX: payload est déjà un array, pas un objet {data: [...]}
        if (Array.isArray(payload)) {
          state.suggestions = payload;
          state.recordsTotal = payload.length;
        } else {
          // Si c'est un objet avec data (pour compatibilité)
          state.suggestions = payload?.data || [];
          state.recordsTotal = payload?.recordsTotal || 0;
        }
        state.loading = false;
      })
      .addCase(fetchSuggestions.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(deleteSuggestions.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSuggestions.fulfilled, (state) => {
        state.isSuccess = true;
        state.loading = false;
      })
      .addCase(deleteSuggestions.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      .addCase(updateSuggestion.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSuggestion.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.loading = false;
        const { id } = action.meta.arg;
        state.suggestions = state.suggestions.map((s) =>
          (s._id === id || s.DT_RowId === id) ? { ...s, isAccepted: true } : s
        );
      })
      .addCase(updateSuggestion.rejected, (state, { payload }) => {
        if (payload?.statusCode === 400) {
          state.errors = payload?.message;
        }
        state.isSuccess = false;
        state.loading = false;
      })
      // Pas de `loading` ici : le tableau resterait vide pendant l'enregistrement
      .addCase(updateSuggestionName.fulfilled, (state, action) => {
        const { id, name } = action.meta.arg;
        state.suggestions = state.suggestions.map((s) =>
          (s._id === id || s.DT_RowId === id) ? { ...s, name: action.payload?.name || name } : s
        );
      });
  }
});

export default servicesSlice.reducer;
